/**
 * auctionLobby.js
 *
 * Helpers for the Auction lobby (the screen before the auction starts).
 *
 * Room status:
 *  -   'lobby' :   Room created, host picks a library and settings, players join teams
 *  -   'live'  :   Host has started the auction
 *                  (onBreak: true while the host has paused it for a break)
 *  -   'submitting' : All items sold, or the host ended it, and teams can have bought more than they submit.
 *                      Teams with extra items pick the ones they submit (team.submission); the host then shows the results.
 *  -   'finished' :   Everyone sees the results.
 *                      (resultSaved: true once the host's device has saved it to SQL)
 * Rooms created before the lobby existed have no status, and are treated as live.
 */

import { doc, Timestamp, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { AuctionRoomsCollection, Teams } from "../../../assets/DatabaseCollections";
import { defaultAuctionSettings } from "../assets/Rules";
import { teamColors } from "../assets/teamColors";
import { getDefaultAttributeRules, libraryForRoom } from "../library/libraries";

export const isRoomInLobby = (room) => room?.status === "lobby";
export const isRoomSubmitting = (room) => room?.status === "submitting";

/**
 * Adds team documents to a batch, for team positions [from, to)
 * @param {Object} batch    -   Firestore write batch
 * @param {String} roomID   -   ID of the room
 * @param {Number} from     -   First team position
 * @param {Number} to       -   One past the last team position
 * @param {Number} budget   -   Starting budget of each team
 */
const addTeamsToBatch = (batch, roomID, from, to, budget) => {
    teamColors.slice(from, to).forEach((color, index) => {
        batch.set(doc(db, AuctionRoomsCollection, roomID, Teams, color.key), {
            name        :   `Team ${color.name}`,
            color       :   color.key,
            order       :   from + index,
            members     :   [],
            budget      :   budget,
            budgetLeft  :   budget,
            items       :   [],
        });
    });
};

/**
 * Sets up a newly created room: creates the teams and opens the lobby.
 * The host picks a library in the lobby.
 * @param {String} roomID   -   ID of the room
 */
export const setUpAuctionRoom = async (roomID) => {
    try {
        const batch = writeBatch(db);
        addTeamsToBatch(batch, roomID, 0, defaultAuctionSettings.maxTeams, defaultAuctionSettings.budgetPerTeam);
        batch.update(doc(db, AuctionRoomsCollection, roomID), {
            status      :   "lobby",
            settings    :   defaultAuctionSettings,
        });
        await batch.commit();
    } catch (error) {
        console.error("Error setting up auction room:", error);
    }
};

/**
 * Picks the library the room will play, and resets the attribute rules to that library's defaults.
 * The room keeps its own copy, so the game isn't affected if the library changes later.
 * @param {String} roomID   -   ID of the room
 * @param {Object} library  -   Library
 * @returns {Boolean}       -   True if saved
 */
export const setRoomLibrary = async (roomID, library) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), {
            library                     :   libraryForRoom(library),
            "settings.attributeRules"   :   getDefaultAttributeRules(library),
        });
        return true;
    } catch (error) {
        console.error("Error setting room library:", error);
        return false;
    }
};

/**
 * Saves the lobby settings. Only used in the lobby, before anything is sold.
 * - Resets every team's budget to the new budget
 * - Adds or removes teams to match the max number of teams (only empty teams are removed)
 * @param {String} roomID   -   ID of the room
 * @param {Object} settings -   New settings
 * @param {Array} teams     -   Teams in the room, sorted (see sortTeams)
 * @returns {Boolean}       -   True if saved
 */
export const saveAuctionSettings = async (roomID, settings, teams) => {
    try {
        const batch = writeBatch(db);
        batch.update(doc(db, AuctionRoomsCollection, roomID), { settings });

        teams.forEach((team, position) => {
            const teamRef = doc(db, AuctionRoomsCollection, roomID, Teams, team.id);
            if (position >= settings.maxTeams) {
                batch.delete(teamRef);
            } else {
                batch.update(teamRef, {
                    budget      :   settings.budgetPerTeam,
                    budgetLeft  :   settings.budgetPerTeam,
                });
            }
        });
        addTeamsToBatch(batch, roomID, teams.length, settings.maxTeams, settings.budgetPerTeam);

        await batch.commit();
        return true;
    } catch (error) {
        console.error("Error saving auction settings:", error);
        return false;
    }
};

/**
 * Puts an item up for auction on everyone's screen.
 * @param {String} roomID       -   ID of the room
 * @param {Number} itemIndex    -   Index in the room's library items
 */
export const showItem = async (roomID, itemIndex) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), { currentMovieIndex: itemIndex });
        return true;
    } catch (error) {
        console.error("Error showing next item:", error);
        return false;
    }
};

/**
 * Skips the current item (nobody bought it) and moves everyone on.
 * @param {String} roomID       -   ID of the room
 * @param {Object} roomUpdates  -   From getRoomUpdatesAfterItem(..., sold = false)
 * @returns {Boolean}           -   True if skipped
 */
export const skipItem = async (roomID, roomUpdates) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), roomUpdates);
        return true;
    } catch (error) {
        console.error("Error skipping item:", error);
        return false;
    }
};

/**
 * Ends a break, putting everyone back on the auction.
 * @param {String} roomID   -   ID of the room
 */
export const resumeAuction = async (roomID) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), { onBreak: false });
        return true;
    } catch (error) {
        console.error("Error resuming auction:", error);
        return false;
    }
};

/**
 * Room fields that end the auction (also used in the same write as the last sale)
 * @returns {Object}    -   Fields to set on the room
 */
export const finishedRoomFields = () => ({ status: "finished", finishedAt: Timestamp.now(), onBreak: false });

/**
 * Room fields for when the last item is done (or the host ends early):
 * the submission step if teams can buy more than they submit, otherwise the results
 * @param {Object} settings -   Room settings
 * @returns {Object}        -   Fields to set on the room
 */
export const endOfAuctionFields = (settings) =>
    settings.itemsToSubmit < settings.maxItemsPerTeam
        ? { status: "submitting", auctionEndedAt: Timestamp.now(), onBreak: false }
        : finishedRoomFields();

/**
 * Ends the auction early: to the submission step, or straight to the results.
 * @param {String} roomID   -   ID of the room
 * @param {Object} settings -   Room settings
 * @returns {Boolean}       -   True if ended
 */
export const finishAuction = async (roomID, settings) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), endOfAuctionFields(settings));
        return true;
    } catch (error) {
        console.error("Error finishing auction:", error);
        return false;
    }
};

/**
 * Saves the items a team submits (positions in its items list). It can change them until the host shows the results.
 * @param {String} roomID       -   ID of the room
 * @param {String} teamID       -   ID of the team
 * @param {Array} submission    -   Item positions, e.g. [0, 2, 3, 4, 5, 6]
 * @returns {Boolean}           -   True if saved
 */
export const saveSubmission = async (roomID, teamID, submission) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID, Teams, teamID), { submission });
        return true;
    } catch (error) {
        console.error("Error saving submission:", error);
        return false;
    }
};

/**
 * Closes the submission step and shows everyone the results.
 * Teams that didn't choose get their best set picked for them.
 * @param {String} roomID   -   ID of the room
 * @returns {Boolean}       -   True if closed
 */
export const showResults = async (roomID) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), finishedRoomFields());
        return true;
    } catch (error) {
        console.error("Error showing results:", error);
        return false;
    }
};

/**
 * Records that the game's result is in the SQL database, so it isn't saved again.
 * @param {String} roomID   -   ID of the room
 */
export const markResultSaved = async (roomID) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), { resultSaved: true });
    } catch (error) {
        console.error("Error marking result saved:", error);
    }
};

/**
 * Starts the auction, moving everyone out of the lobby.
 * @param {String} roomID   -   ID of the room
 * @returns {Boolean}       -   True if started
 */
export const startAuction = async (roomID) => {
    try {
        await updateDoc(doc(db, AuctionRoomsCollection, roomID), {
            status              :   "live",
            startedAt           :   Timestamp.now(),
            currentMovieIndex   :   0,
            round               :   1,
            unsoldItems         :   [],
        });
        return true;
    } catch (error) {
        console.error("Error starting auction:", error);
        return false;
    }
};
